import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  ClipboardCheck, Circle, CheckCircle2, Plus, Loader2, X, Edit,
  Calendar, Users, RefreshCw, ChevronRight, FileText, Handshake,
  Target, Video, Settings, Truck, Star
} from "lucide-react";

const CATEGORY_META = {
  setup: { icon: Settings, color: "text-blue-600", bg: "bg-blue-50", label: "Setup" },
  documents: { icon: FileText, color: "text-amber-600", bg: "bg-amber-50", label: "Documents" },
  meetings: { icon: Video, color: "text-purple-600", bg: "bg-purple-50", label: "Meetings" },
  strategy: { icon: Target, color: "text-emerald-600", bg: "bg-emerald-50", label: "Strategy" },
  delivery: { icon: Truck, color: "text-indigo-600", bg: "bg-indigo-50", label: "Delivery" },
  custom: { icon: Star, color: "text-pink-600", bg: "bg-pink-50", label: "Custom" },
};

function ChecklistItem({ item, onToggle, onDelete }) {
  const cat = CATEGORY_META[item.category] || CATEGORY_META.custom;
  const CatIcon = cat.icon;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all group ${item.done ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-neutral-200 hover:border-neutral-300"}`} data-testid={`onb-item-${item.item_id}`}>
      <button onClick={() => onToggle(item)} className="flex-shrink-0" data-testid={`onb-toggle-${item.item_id}`}>
        {item.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-neutral-300 hover:text-emerald-400 transition-colors" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${item.done ? "line-through text-emerald-700/70" : "text-neutral-800"}`}>{item.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] flex items-center gap-0.5 ${cat.color}`}><CatIcon className="w-2.5 h-2.5" />{cat.label}</span>
          {item.completed_at && <span className="text-[10px] text-emerald-600 flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{new Date(item.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
          {item.notes && <span className="text-[10px] text-neutral-400 truncate max-w-[120px]">{item.notes}</span>}
        </div>
      </div>
      <button onClick={() => onDelete(item)} className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 text-neutral-400 hover:text-red-500 transition-all" data-testid={`onb-delete-${item.item_id}`}><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function ChecklistCard({ checklist, onClick }) {
  const progress = checklist.progress || 0;
  return (
    <Card className="hover:shadow-sm transition-shadow cursor-pointer border-neutral-200" onClick={onClick} data-testid={`checklist-card-${checklist.checklist_id}`}>
      <CardContent className="py-4 px-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">{checklist.client_name}</h3>
          {checklist.status === "completed" ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Complete</Badge> : <Badge variant="outline" className="text-[10px]">{checklist.done_count}/{checklist.total_count}</Badge>}
        </div>
        <Progress value={progress} className="h-2 mb-1" />
        <p className="text-[10px] text-neutral-400 text-right tabular-nums">{progress}%</p>
      </CardContent>
    </Card>
  );
}

export function OnboardingChecklistPanel({ open, onOpenChange }) {
  const [checklists, setChecklists] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [clients, setClients] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateClientId, setGenerateClientId] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/onboarding-checklists`);
      if (res.ok) { const d = await res.json(); setChecklists(d.checklists || []); setStats(d.stats || {}); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    try {
      const res = await authFetch(`${API}/consultant/onboarding-checklists/${id}`);
      if (res.ok) setDetailData(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchClients = async () => {
    try {
      const res = await authFetch(`${API}/consultant/clients`);
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (open) { fetchAll(); fetchClients(); } }, [open]);

  const openDetail = (cl) => { setSelectedChecklist(cl.checklist_id); fetchDetail(cl.checklist_id); };

  const handleToggle = async (item) => {
    try {
      const res = await authFetch(`${API}/consultant/onboarding-checklists/${selectedChecklist}/items/${item.item_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ done: !item.done })
      });
      if (res.ok) {
        const d = await res.json();
        setDetailData(prev => ({ ...prev, items: d.items, progress: d.progress, status: d.status, done_count: d.done_count, total_count: d.total_count }));
        fetchAll();
        toast.success(item.done ? "Reopened" : "Completed!");
      }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    setAdding(true);
    try {
      const res = await authFetch(`${API}/consultant/onboarding-checklists/${selectedChecklist}/items`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newItemTitle.trim() })
      });
      if (res.ok) { setNewItemTitle(""); fetchDetail(selectedChecklist); fetchAll(); toast.success("Item added"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    setAdding(false);
  };

  const handleDeleteItem = async (item) => {
    try {
      const res = await authFetch(`${API}/consultant/onboarding-checklists/${selectedChecklist}/items/${item.item_id}`, { method: "DELETE" });
      if (res.ok) { fetchDetail(selectedChecklist); fetchAll(); toast.success("Removed"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleGenerate = async () => {
    if (!generateClientId) { toast.error("Select a client"); return; }
    try {
      const res = await authFetch(`${API}/consultant/onboarding-checklists/generate/${generateClientId}`, { method: "POST" });
      if (res.ok) { toast.success("Checklist created"); setShowGenerate(false); setGenerateClientId(""); fetchAll(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  // Clients without checklists
  const clientsWithChecklist = new Set(checklists.map(c => c.client_id));
  const clientsWithout = clients.filter(c => !clientsWithChecklist.has(c.user_id));

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="onboarding-panel">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              {selectedChecklist ? (
                <><Button variant="ghost" size="sm" onClick={() => { setSelectedChecklist(null); setDetailData(null); }} className="mr-1"><ChevronRight className="w-4 h-4 rotate-180" /></Button>{detailData?.client_name || "Checklist"}</>
              ) : (
                <><ClipboardCheck className="w-5 h-5 text-emerald-600" />Client Onboarding</>
              )}
            </span>
            {!selectedChecklist && (
              <div className="flex gap-2">
                {clientsWithout.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => { setShowGenerate(true); }} data-testid="generate-checklist-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />Generate Checklist
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={fetchAll} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
              </div>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Manage client onboarding checklists</DialogDescription>
        </DialogHeader>

        {loading && !selectedChecklist ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : selectedChecklist && detailData ? (
          /* ===== DETAIL VIEW ===== */
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
            <div className="space-y-4">
              {/* Progress */}
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="py-4 px-5">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-base font-semibold text-emerald-800">{detailData.done_count}/{detailData.total_count} Steps Complete</h3>
                      <p className="text-xs text-emerald-600">
                        {detailData.status === "completed" ? "Onboarding complete!" : "Track client onboarding progress"}
                      </p>
                    </div>
                    <span className="text-3xl font-bold text-emerald-700 tabular-nums" data-testid="onb-progress-pct">{detailData.progress}%</span>
                  </div>
                  <Progress value={detailData.progress} className="h-3" />
                </CardContent>
              </Card>

              {/* Add Item */}
              <div className="flex gap-2">
                <Input value={newItemTitle} onChange={e => setNewItemTitle(e.target.value)} placeholder="Add custom step..." className="flex-1" data-testid="onb-add-input" onKeyDown={e => { if (e.key === "Enter") handleAddItem(); }} />
                <Button onClick={handleAddItem} disabled={adding || !newItemTitle.trim()} data-testid="onb-add-btn">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</Button>
              </div>

              {/* Items */}
              <div className="space-y-1.5">
                {(detailData.items || []).sort((a, b) => (a.order || 99) - (b.order || 99)).map(item => (
                  <ChecklistItem key={item.item_id} item={item} onToggle={handleToggle} onDelete={handleDeleteItem} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ===== LIST VIEW ===== */
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3" data-testid="onb-stats">
                <Card className="border-0 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-2xl font-bold tabular-nums">{stats.total || 0}</p><p className="text-[10px] text-neutral-500">Total</p></CardContent></Card>
                <Card className="border-0 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-2xl font-bold text-amber-600 tabular-nums">{stats.in_progress || 0}</p><p className="text-[10px] text-neutral-500">In Progress</p></CardContent></Card>
                <Card className="border-0 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-2xl font-bold text-emerald-600 tabular-nums">{stats.completed || 0}</p><p className="text-[10px] text-neutral-500">Completed</p></CardContent></Card>
                <Card className="border-0 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-2xl font-bold text-blue-600 tabular-nums">{stats.avg_progress || 0}%</p><p className="text-[10px] text-neutral-500">Avg Progress</p></CardContent></Card>
              </div>

              {/* Checklist Cards */}
              {checklists.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {checklists.map(cl => <ChecklistCard key={cl.checklist_id} checklist={cl} onClick={() => openDetail(cl)} />)}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-400">
                  <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="mb-2">No onboarding checklists yet</p>
                  <p className="text-xs">Checklists are auto-created when new clients are added</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generate Checklist for Existing Client */}
        {showGenerate && (
          <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Generate Checklist</DialogTitle>
                <DialogDescription>Create an onboarding checklist for an existing client</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <Label>Select Client</Label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={generateClientId} onChange={e => setGenerateClientId(e.target.value)} data-testid="generate-client-select">
                  <option value="">Choose a client...</option>
                  {clientsWithout.map(c => <option key={c.user_id} value={c.user_id}>{c.name} ({c.email})</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowGenerate(false)}>Cancel</Button>
                <Button onClick={handleGenerate} data-testid="generate-submit-btn"><Plus className="w-4 h-4 mr-1" />Generate</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
